import type { QRAnswerItem } from '@scripsteam/scrips-signal-ds';
import type { SoapPayload } from './ambient-types';
import { hasStepSectioning, linkIdInSection } from '../lib/questionnaire-groups';

/**
 * The ambient narratives, written into the encounter's QuestionnaireResponse.
 *
 * WHY THIS EXISTS. Samer, on the live dev app: "The transcript is still limited to assessment and
 * plan and order. It doesn't fill any of the other templates." He is right, and the cause is not a
 * missing feature — it is that three fields already arriving on the wire are read by nothing.
 * `SoapPayload.chiefComplaint`, `.historyOfPresentIllness` and `.assessmentNarrative` appear in
 * exactly ONE file on `origin/main`: their own type declaration. The server sends them; the client
 * discards them.
 *
 * ── WHY IT COULD NOT SIMPLY BE WIRED UP ────────────────────────────────────────────────────────
 *
 * The note is derived ENTIRELY from a QuestionnaireResponse against a Template-Studio-authored
 * Questionnaire (`generated-note.ts`). Sections are template groups. So prose can only reach the
 * note as an ANSWER to an item — and there was nothing to answer:
 *
 *   - Template sections carry no clinical identity. Real Template Studio output authors
 *     author-named groups (`sec-<guid>` "Health"), so nothing downstream can find "the HPI".
 *   - The 91 free-text items in the checked-in template are all "If other, please specify" /
 *     "If yes, please list" — conditional follow-ups to a specific question. There is no narrative
 *     slot anywhere. Putting an HPI into "Other oils" is not a degraded outcome, it is a wrong one.
 *
 * ── THE APPROACH: ONE AMBIENT TEMPLATE, NOT A TAGGED ESTATE ───────────────────────────────────
 *
 * Rather than retro-tagging every existing template (coordination with every author, and a
 * Template Studio change), ONE template is authored whose sections ARE the narrative slots ambient
 * produces. A clinician who works with ambient selects it at runtime; every other template is
 * untouched. Same shape Heidi uses.
 *
 * That makes this module a CONTRACT, not a heuristic: it writes to the linkIds below and nothing
 * else. `AMBIENT_NARRATIVE_SLOTS` is the thing a template must declare to be ambient-compatible,
 * and it is deliberately tiny — three items — because every one is a promise to a template author.
 *
 * ── WHAT IT REFUSES TO DO ─────────────────────────────────────────────────────────────────────
 *
 *   1. **Never writes a linkId the template does not declare.** An answer to a question that does
 *      not exist cannot be rendered, cannot be edited, and would sit in the response invisibly
 *      until something else tripped over it.
 *   2. **Never overwrites what a clinician typed.** Ambient proposes; the clinician owns the note.
 *      A non-empty existing answer is left exactly as it is, every time, with no merge and no
 *      append — appending would silently double a narrative on a second analysis run.
 *   3. **Never writes prose into a non-prose item.** A slot must be `text` or `string`. Writing a
 *      paragraph into a `boolean` or a `choice` produces an answer the renderer cannot display and
 *      the note walker mis-formats.
 *   4. **Marks every answer `aiAuthored`.** The DS decorates these per DS-021, so a clinician can
 *      see at a glance which prose they wrote and which the model did. An unmarked AI narrative in
 *      a clinical note is the thing that must never ship.
 *
 * ── REAL TEMPLATES: THE HPI GOES TO THE TEMPLATE'S OWN HPI NARRATIVE (PROD-2537) ─────────────
 *
 * The reserved `ambient-` slots above are declared by exactly one template — the test fixture
 * `__fixtures__/ambient-template.json`. No real Template Studio template on dev declares them, so
 * against every template a clinician can actually pick, ambient wrote nothing. Two ways out: edit a
 * shared dev template to add the reserved linkIds, or adapt to what templates ALREADY declare. This
 * module adapts, for three reasons:
 *
 *   - **The Studio already has an HPI narrative convention.** Its default encounter templates
 *     author a free-text `-narrative` leaf in the HPI section — `hpi-cc-narrative`, "History of
 *     present illness (narrative)" — and the PA already binds the HPI step's ADDED NOTES editor to
 *     exactly that item (`findNarrativeItem`, PROD-1368). Writing the dictated HPI there puts it in
 *     the box the clinician already reads and edits, not in a second HPI field beside it.
 *   - **It covers every template that follows the convention**, not one hand-edited template. On
 *     the 2026-09-11 dev capture that was five of ten (`hpi-cc-narrative` ×4, `hpi-visit-narrative`).
 *   - **No shared dev data changes.** Editing a template other practices use to suit one feature
 *     is a change to their notes too.
 *
 * The reserved slot still WINS when a template declares it, so the ambient template keeps working
 * exactly as before. The fallback is chosen by the SAME rule as the ADDED NOTES editor (first
 * non-group `-narrative` leaf of the HPI section), and is refused unless that item is itself in the
 * HPI section (`hpi-` prefix) — so on an unsectioned template it can never land in, say, a ROS note.
 *
 * THE CHIEF COMPLAINT HAS NO FALLBACK, on purpose. On a real template the complaint's system of
 * record is the patient's complaint document (`CreateChiefComplaint` → `PatientComplaints`), which
 * sign/lock requires and NABIDH/Malaffi read for PV2 — the Studio's `chief-complaint-hpi` block
 * renders it from there. A second copy in a template item (`hpi-cc-text`) could disagree with it,
 * and under the block marker the DS does not even draw that item. The dictated complaint reaches
 * the record through the B1 accept path, not through this module.
 */

/**
 * The only part of a questionnaire this module reads.
 *
 * Structural on purpose. The host holds a `FhirQuestionnaire` (nullable `title`) while the DS
 * renderer takes its own `Questionnaire`, and the two are not assignable to each other. Depending
 * on either would force a cast at the call site, which is exactly where a shape mismatch stops
 * being visible. Nothing here needs a title — only linkIds, types and nesting.
 */
export interface TemplateItemLike {
  linkId?: string | null;
  type?: string | null;
  readOnly?: boolean | null;
  item?: readonly TemplateItemLike[] | null;
}
export interface TemplateLike {
  item?: readonly TemplateItemLike[] | null;
}

/** What kind of narrative a slot holds. Named so a template author knows what lands where. */
export type NarrativeSlot = 'chiefComplaint' | 'historyOfPresentIllness';

/**
 * THE CONTRACT. A template is ambient-compatible when it declares these linkIds as `text` items.
 *
 * Prefixed `ambient-` on purpose: the prefix is the declaration of intent. A template author who
 * writes `ambient-hpi` is saying "put the dictated history here", and nothing else in the estate
 * can collide with it by accident. Stable — changing one of these strings silently stops a template
 * receiving its narrative, so they are treated as a published interface.
 */
export const AMBIENT_NARRATIVE_SLOTS: Readonly<Record<NarrativeSlot, string>> = {
  chiefComplaint: 'ambient-chief-complaint',
  historyOfPresentIllness: 'ambient-hpi',
};

/**
 * `assessmentNarrative` IS DELIBERATELY NOT A SLOT, and this is a correction.
 *
 * The first version of this contract had one, targeting a group called `ap-assessment`. Cross-model
 * review (Codex, gpt-5.6-sol) showed that group can never render: `QUESTIONNAIRE_STEPS` is
 * `['hpi','risk','ros','pe']` and the A/P is a separate canvas, not a questionnaire section. So an
 * `hpi` group in the template switches on step-sectioning, and `ap-assessment` is then filtered out
 * of EVERY `QuestionnaireRenderer` — the model's assessment would have been written into the note
 * with no surface on which a clinician could read, edit or delete it.
 *
 * An AI narrative in a clinical record that cannot be edited is worse than one that is missing.
 *
 * The right home already exists: `assessment-plan-view.tsx` has an assessment-narrative popup
 * (Andrew, 2026-08-05) off the A/P section header. Routing the dictated assessment there is its own
 * change against a surface that renders, not a linkId in this contract.
 */

/** The item types that can hold a narrative. Anything else is refused. */
const PROSE_TYPES: ReadonlySet<string> = new Set(['text', 'string']);

/** The Studio's narrative-leaf convention — the same suffix `findNarrativeItem` keys on (PROD-1368). */
const NARRATIVE_LINKID_SUFFIX = '-narrative';

/** First non-group leaf whose linkId ends `-narrative`, depth-first — `findNarrativeItem`'s rule. */
function firstNarrativeLeaf(items: readonly TemplateItemLike[] | null | undefined): TemplateItemLike | undefined {
  for (const it of items ?? []) {
    if (it.type !== 'group' && typeof it.linkId === 'string' && it.linkId.endsWith(NARRATIVE_LINKID_SUFFIX)) return it;
    const nested = firstNarrativeLeaf(it.item);
    if (nested) return nested;
  }
  return undefined;
}

/**
 * The template's OWN HPI narrative item (PROD-2537) — the item the HPI step's ADDED NOTES editor is
 * bound to, or `undefined`. Scoped exactly as the renderer scopes the HPI step: the `hpi`-prefixed
 * top-level groups when the template is step-sectioned, the whole template when it is not. The
 * result must itself sit in the HPI section, so an unsectioned template's first `-narrative` leaf
 * being, say, `ros-general-narrative` yields nothing rather than a ROS note full of HPI.
 */
export function findHpiNarrativeItem(template: TemplateLike | undefined): TemplateItemLike | undefined {
  const top = template?.item ?? [];
  const section = hasStepSectioning(top)
    ? top.filter((g) => typeof g.linkId === 'string' && linkIdInSection(g.linkId, 'hpi'))
    : top;
  const found = firstNarrativeLeaf(section);
  return found && typeof found.linkId === 'string' && linkIdInSection(found.linkId, 'hpi') ? found : undefined;
}

/**
 * Where a slot's narrative goes on this template: the reserved `ambient-` linkId when declared,
 * else — for the HPI only — the template's own HPI narrative item. `undefined` = nowhere.
 */
function resolveSlotItem(
  slot: NarrativeSlot,
  items: Map<string, TemplateItemLike>,
  template: TemplateLike,
): TemplateItemLike | undefined {
  const reserved = items.get(AMBIENT_NARRATIVE_SLOTS[slot]);
  if (reserved) return reserved;
  return slot === 'historyOfPresentIllness' ? findHpiNarrativeItem(template) : undefined;
}

/** A slot item that can safely receive prose: prose-typed, not read-only, with a linkId. */
function isWritableProse(item: TemplateItemLike | undefined): item is TemplateItemLike & { linkId: string } {
  return !!item && typeof item.linkId === 'string' && !!item.type && PROSE_TYPES.has(item.type) && !item.readOnly;
}

/** Flatten a template to linkId -> item, at any depth (groups nest arbitrarily since PROD-2005). */
function indexItems(items: readonly TemplateItemLike[] | null | undefined): Map<string, TemplateItemLike> {
  const map = new Map<string, TemplateItemLike>();
  const walk = (list: readonly TemplateItemLike[] | null | undefined) => {
    for (const it of list ?? []) {
      if (it.linkId) map.set(it.linkId, it);
      walk(it.item);
    }
  };
  walk(items);
  return map;
}

/** An existing answer counts as the clinician's whenever it holds anything but whitespace. */
function hasContent(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * The narrative answers to ADD to the response. Pure — returns only what is new.
 *
 * Returns `[]` whenever there is nothing safe to write, which is the common case for a template
 * that is not ambient-compatible. That is the correct outcome, not a failure: ambient keeps filling
 * the Assessment & Plan exactly as it does today and the rest of the note stays the clinician's.
 *
 * @param soap      the mode-8 payload off the wire
 * @param template  the encounter's Questionnaire, used to check each slot EXISTS and is prose
 * @param existing  answers already on the response — never overwritten
 */
export function toNarrativeAnswers(
  soap: SoapPayload | undefined,
  template: TemplateLike | undefined,
  existing: readonly QRAnswerItem[] = [],
): QRAnswerItem[] {
  return toNarrativeOffers(soap, template, existing).map((o) => ({ linkId: o.linkId, value: o.text, aiAuthored: true }));
}

/**
 * One dictated narrative the physician can accept (PROD-3195): the slot it is for, the linkId it
 * would go to, and the text. Nothing is written from it until the physician clicks Accept.
 */
export interface NarrativeOffer {
  slot: NarrativeSlot;
  linkId: string;
  text: string;
}

/**
 * The narratives that WOULD be written, as offers. Pure. Same slot resolution and same refusals as
 * `toNarrativeAnswers` (which is now built on this): the template must declare a writable prose slot,
 * and a slot the physician has answered never gets an offer.
 */
export function toNarrativeOffers(
  soap: SoapPayload | undefined,
  template: TemplateLike | undefined,
  existing: readonly QRAnswerItem[] = [],
): NarrativeOffer[] {
  if (!soap || !template) return [];

  const items = indexItems(template.item);
  const answered = new Set(existing.filter((a) => hasContent(a.value)).map((a) => a.linkId));
  const out: NarrativeOffer[] = [];

  for (const slot of Object.keys(AMBIENT_NARRATIVE_SLOTS) as NarrativeSlot[]) {
    const text = soap[slot];
    if (typeof text !== 'string' || !text.trim()) continue;

    // (1) the template must actually ask this question — the reserved slot, or (HPI only) the
    // template's own HPI narrative item (PROD-2537).
    const item = resolveSlotItem(slot, items, template);

    // (3) it must be able to hold prose. A read-only item is a computed field: writing to it would
    // be overwritten on the next recompute and would look like data loss.
    if (!isWritableProse(item)) continue;
    const linkId = item.linkId;

    // (2) the clinician's text always wins
    if (answered.has(linkId)) continue;

    out.push({ slot, linkId, text: text.trim() });
  }

  return out;
}

/** True when an answer holds anything but whitespace. Exported for the host's click-time check. */
export const narrativeHasContent = hasContent;

/** How each slot is named to a clinician when its dictated value is contested (PROD-2445). */
export const NARRATIVE_SLOT_LABEL: Readonly<Record<NarrativeSlot, string>> = {
  chiefComplaint: 'Chief complaint',
  historyOfPresentIllness: 'History of present illness',
};

/**
 * A dictated narrative that lost to an answer already in its slot (PROD-2445).
 *
 * `key` identifies THIS collision — the slot and the dictated wording — so a choice the clinician
 * made is not asked again when a re-analysis delivers the same wording. A re-analysis with DIFFERENT
 * wording is a different collision, and is asked.
 */
export interface NarrativeCollision {
  key: string;
  slot: NarrativeSlot;
  linkId: string;
  /** What the slot holds now — the clinician's. Never overwritten by this module. */
  existing: string;
  /** What ambient heard. */
  dictated: string;
}

/** Stable identity for a collision: the slot and the dictated wording. */
export function narrativeCollisionKey(linkId: string, dictated: string): string {
  return `${linkId}::${dictated}`;
}

/**
 * THE LOSERS OF RULE (2), so they are not silently dropped (PROD-2445).
 *
 * `toNarrativeAnswers` keeps a non-empty existing answer exactly as it is, and that rule stands —
 * this function does not change it and writes nothing. What was wrong is that the dictated value
 * VANISHED: a clinician could not tell "ambient found nothing for this slot" from "ambient found
 * something and dropped it", and a dictated correction to a stale answer was thrown away unseen.
 *
 * So the same walk, the same slot resolution and the same refusals (the slot must exist, be prose,
 * not be read-only), and it
 * returns what `toNarrativeAnswers` skipped because the slot was answered — each paired with the
 * answer it collided with, for the clinician to see both and choose.
 *
 * An answer that already says the same thing (after trimming) is NOT a collision: there is nothing
 * to choose between, and asking would train the clinician to click through the question.
 */
export function findNarrativeCollisions(
  soap: SoapPayload | undefined,
  template: TemplateLike | undefined,
  existing: readonly QRAnswerItem[] = [],
): NarrativeCollision[] {
  if (!soap || !template) return [];

  const items = indexItems(template.item);
  const out: NarrativeCollision[] = [];

  for (const slot of Object.keys(AMBIENT_NARRATIVE_SLOTS) as NarrativeSlot[]) {
    const text = soap[slot];
    if (typeof text !== 'string' || !text.trim()) continue;

    // The SAME slot resolution as toNarrativeAnswers — the reserved slot, or (HPI only) the
    // template's own HPI narrative item (PROD-2537) — so the two can never disagree about which item
    // a dictated narrative was aimed at.
    const item = resolveSlotItem(slot, items, template);
    if (!isWritableProse(item)) continue;
    const linkId = item.linkId;

    const answer = existing.find((a) => a.linkId === linkId && hasContent(a.value));
    if (!answer) continue; // unanswered: toNarrativeAnswers fills it, nothing is contested

    const current = typeof answer.value === 'string' ? answer.value.trim() : String(answer.value);
    const dictated = text.trim();
    if (current === dictated) continue;

    out.push({ key: narrativeCollisionKey(linkId, dictated), slot, linkId, existing: current, dictated });
  }

  return out;
}

/**
 * Which slots a template supports — for telling a clinician why their note did not fill.
 *
 * Exported because "nothing happened" is the worst possible answer to give someone whose
 * consultation just failed to become a note. A surface can use this to say "this template has no
 * ambient sections" instead of leaving them guessing.
 */
export function supportedSlots(template: TemplateLike | undefined): NarrativeSlot[] {
  if (!template) return [];
  const items = indexItems(template.item);
  return (Object.keys(AMBIENT_NARRATIVE_SLOTS) as NarrativeSlot[]).filter((slot) =>
    isWritableProse(resolveSlotItem(slot, items, template)),
  );
}
